import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, ArrowRight, Grid, LayoutGrid, RefreshCw } from 'lucide-react';

const DRIVE_API_KEY = process.env.REACT_APP_GOOGLE_DRIVE_API_KEY || '';
const ROOT_FOLDER_ID = '12IAwViw8k7Ow9EG7SZ6prD6PcbGuDnq0';
const DRIVE_FILES_ENDPOINT = 'https://www.googleapis.com/drive/v3/files';
const fallbackHero = 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1920&q=80';

const driveList = async (query) => {
  if (!DRIVE_API_KEY) throw new Error('Google Drive API key is not configured.');

  const params = new URLSearchParams({
    q: query,
    key: DRIVE_API_KEY,
    pageSize: '1000',
    orderBy: 'name',
    fields: 'files(id,name,mimeType,parents),nextPageToken',
    spaces: 'drive',
  });

  const response = await fetch(`${DRIVE_FILES_ENDPOINT}?${params.toString()}`, { cache: 'no-store' });
  const data = await response.json();

  if (!response.ok) {
    throw new Error(data?.error?.message || `Google Drive API request failed (${response.status})`);
  }

  return data.files || [];
};

const imageUrl = (fileId) => `https://drive.google.com/thumbnail?id=${encodeURIComponent(fileId)}&sz=w1600`;

const cleanTitle = (filename) => String(filename || 'InArch Design Project')
  .replace(/\.[^/.]+$/, '')
  .replace(/[_-]+/g, ' ')
  .replace(/\s+/g, ' ')
  .trim()
  .replace(/\b\w/g, (letter) => letter.toUpperCase());

const normalizeCategory = (name) => String(name || 'other').trim().toLowerCase().replace(/\s+/g, '-');

const collectImages = async (folderId, category, results) => {
  const children = await driveList(`'${folderId}' in parents and trashed = false`);

  for (const child of children) {
    if (child.mimeType === 'application/vnd.google-apps.folder') {
      await collectImages(child.id, normalizeCategory(child.name), results);
      continue;
    }

    if (child.mimeType && child.mimeType.startsWith('image/')) {
      results.push({
        id: child.id,
        category,
        image: imageUrl(child.id),
        title: cleanTitle(child.name),
        location: '',
        description: 'Interior design by InArch Design Studio.',
      });
    }
  }
};

const GalleryPage = () => {
  const [activeFilter, setActiveFilter] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [galleryItems, setGalleryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lightbox, setLightbox] = useState({ isOpen: false, currentIndex: 0 });

  const fetchGallery = useCallback(async () => {
    setLoading(true);
    setError('');

    try {
      const items = [];
      const rootChildren = await driveList(`'${ROOT_FOLDER_ID}' in parents and trashed = false`);

      for (const child of rootChildren) {
        if (child.mimeType === 'application/vnd.google-apps.folder') {
          await collectImages(child.id, normalizeCategory(child.name), items);
        } else if (child.mimeType && child.mimeType.startsWith('image/')) {
          items.push({
            id: child.id,
            category: 'other',
            image: imageUrl(child.id),
            title: cleanTitle(child.name),
            location: '',
            description: 'Interior design by InArch Design Studio.',
          });
        }
      }

      setGalleryItems(items);
      if (!items.length) setError('No gallery images were found in the Inarch Website Google Drive folder.');
    } catch (err) {
      console.error('Google Drive gallery error:', err);
      setError(err.message || 'Unable to load the Google Drive gallery.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchGallery();
  }, [fetchGallery]);

  const filters = useMemo(() => {
    const preferred = [
      ['all', 'ALL'],
      ['living-room', 'LIVING ROOM'],
      ['bedroom', 'BEDROOM'],
      ['kitchen', 'KITCHEN'],
      ['office', 'OFFICE'],
      ['bathroom', 'BATHROOM'],
      ['commercial', 'COMMERCIAL'],
      ['outdoor', 'OUTDOOR'],
    ];
    const existing = new Set(galleryItems.map((item) => item.category));
    const dynamic = [...existing]
      .filter((category) => category !== 'other' && !preferred.some(([id]) => id === category))
      .map((category) => [category, category.replace(/-/g, ' ').toUpperCase()]);
    return [...preferred.filter(([id]) => id === 'all' || existing.has(id)), ...dynamic];
  }, [galleryItems]);

  const filteredItems = useMemo(
    () => activeFilter === 'all' ? galleryItems : galleryItems.filter((item) => item.category === activeFilter),
    [activeFilter, galleryItems]
  );

  useEffect(() => {
    if (activeFilter !== 'all' && !filters.some(([id]) => id === activeFilter)) setActiveFilter('all');
  }, [activeFilter, filters]);

  const openLightbox = (index) => {
    setLightbox({ isOpen: true, currentIndex: index });
    document.body.style.overflow = 'hidden';
  };

  const closeLightbox = useCallback(() => {
    setLightbox({ isOpen: false, currentIndex: 0 });
    document.body.style.overflow = 'auto';
  }, []);

  const nextImage = useCallback(() => {
    setLightbox((prev) => ({ ...prev, currentIndex: filteredItems.length ? (prev.currentIndex + 1) % filteredItems.length : 0 }));
  }, [filteredItems.length]);

  const prevImage = useCallback(() => {
    setLightbox((prev) => ({ ...prev, currentIndex: filteredItems.length ? (prev.currentIndex - 1 + filteredItems.length) % filteredItems.length : 0 }));
  }, [filteredItems.length]);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!lightbox.isOpen) return;
      if (event.key === 'Escape') closeLightbox();
      if (event.key === 'ArrowRight') nextImage();
      if (event.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightbox.isOpen, closeLightbox, nextImage, prevImage]);

  return (
    <div>
      <section className="relative min-h-[50vh] flex items-center bg-[#2C2C2C]">
        <div className="absolute inset-0 opacity-30">
          <img src={galleryItems[0]?.image || fallbackHero} alt="InArch Design Studio gallery" className="w-full h-full object-cover" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32 pt-40">
          <span className="text-[#D4956A] text-sm tracking-[0.2em] uppercase mb-4 block">OUR PORTFOLIO</span>
          <h1 className="font-serif text-5xl md:text-6xl text-white mb-6">Spaces We've <span className="text-[#D4956A] italic">Transformed</span></h1>
          <p className="text-white/70 text-lg max-w-2xl">Explore our collection of thoughtfully designed interiors, automatically updated from our Google Drive project gallery.</p>
        </div>
      </section>

      <section className="py-16 bg-[#FDFBF7]">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6 mb-12">
            <div className="flex flex-wrap justify-center gap-3">
              {filters.map(([id, label]) => (
                <button key={id} onClick={() => setActiveFilter(id)} className={`px-5 py-2 text-sm tracking-wider transition-all duration-300 rounded ${activeFilter === id ? 'bg-[#B87333] text-white' : 'bg-white text-[#6B6B6B] hover:text-[#B87333] border border-[#E8E3DB]'}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button aria-label="Grid view" onClick={() => setViewMode('grid')} className={`p-2 rounded ${viewMode === 'grid' ? 'bg-[#B87333] text-white' : 'bg-white text-[#6B6B6B] border border-[#E8E3DB]'}`}><Grid size={20} /></button>
              <button aria-label="Masonry view" onClick={() => setViewMode('masonry')} className={`p-2 rounded ${viewMode === 'masonry' ? 'bg-[#B87333] text-white' : 'bg-white text-[#6B6B6B] border border-[#E8E3DB]'}`}><LayoutGrid size={20} /></button>
            </div>
          </div>

          <div className="flex items-center justify-between mb-8">
            <p className="text-[#6B6B6B] text-sm">{loading ? 'Loading projects…' : `Showing ${filteredItems.length} projects`}</p>
            <button onClick={fetchGallery} className="inline-flex items-center gap-2 text-sm text-[#B87333] hover:opacity-70" disabled={loading}>
              <RefreshCw size={16} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
          </div>

          {error && (
            <div className="mb-8 rounded-lg border border-[#E8E3DB] bg-white p-6 text-center text-[#6B6B6B]">
              <p>{error}</p>
              {!DRIVE_API_KEY && <p className="mt-2 text-xs text-[#999]">Add REACT_APP_GOOGLE_DRIVE_API_KEY before building the frontend.</p>}
            </div>
          )}

          {!loading && !error && filteredItems.length > 0 && (
            viewMode === 'grid' ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {filteredItems.map((item, index) => (
                  <div key={item.id} onClick={() => openLightbox(index)} className="group relative overflow-hidden rounded-lg cursor-pointer bg-white shadow-md hover:shadow-xl transition-shadow duration-300">
                    <div className="aspect-[4/3] overflow-hidden bg-[#EEEAE4]"><img src={item.image} alt={item.title} loading="lazy" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" /></div>
                    <div className="p-4"><h3 className="font-serif text-lg text-[#2C2C2C] mb-1">{item.title}</h3>{item.location && <p className="text-[#B87333] text-sm">{item.location}</p>}</div>
                    <div className="absolute inset-0 bg-[#B87333]/0 group-hover:bg-[#B87333]/10 transition-colors duration-300" />
                  </div>
                ))}
              </div>
            ) : (
              <div className="columns-1 sm:columns-2 lg:columns-3 xl:columns-4 gap-6">
                {filteredItems.map((item, index) => (
                  <div key={item.id} onClick={() => openLightbox(index)} className="group relative overflow-hidden rounded-lg cursor-pointer mb-6 break-inside-avoid bg-white shadow-md hover:shadow-xl transition-shadow duration-300">
                    <div className="overflow-hidden bg-[#EEEAE4]"><img src={item.image} alt={item.title} loading="lazy" className="w-full object-cover transition-transform duration-700 group-hover:scale-110" /></div>
                    <div className="p-4"><h3 className="font-serif text-lg text-[#2C2C2C] mb-1">{item.title}</h3>{item.location && <p className="text-[#B87333] text-sm">{item.location}</p>}</div>
                  </div>
                ))}
              </div>
            )
          )}

          {!loading && !error && filteredItems.length === 0 && <div className="py-20 text-center text-[#6B6B6B]">No projects found in this category.</div>}
        </div>
      </section>

      {lightbox.isOpen && filteredItems.length > 0 && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center" onClick={closeLightbox}>
          <button aria-label="Close gallery" className="absolute top-6 right-6 text-white/80 hover:text-white z-10 p-2" onClick={closeLightbox}><X size={32} /></button>
          <button aria-label="Previous image" className="absolute left-4 md:left-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white z-10 bg-black/30 hover:bg-black/50 p-3 rounded-full" onClick={(e) => { e.stopPropagation(); prevImage(); }}><ChevronLeft size={32} /></button>
          <button aria-label="Next image" className="absolute right-4 md:right-8 top-1/2 -translate-y-1/2 text-white/80 hover:text-white z-10 bg-black/30 hover:bg-black/50 p-3 rounded-full" onClick={(e) => { e.stopPropagation(); nextImage(); }}><ChevronRight size={32} /></button>
          <div className="max-w-5xl mx-4" onClick={(e) => e.stopPropagation()}>
            <img src={filteredItems[lightbox.currentIndex]?.image} alt={filteredItems[lightbox.currentIndex]?.title} className="max-h-[70vh] max-w-full w-auto mx-auto rounded-lg shadow-2xl" />
            <div className="text-center mt-6"><h3 className="font-serif text-2xl md:text-3xl text-white mb-2">{filteredItems[lightbox.currentIndex]?.title}</h3>{filteredItems[lightbox.currentIndex]?.location && <p className="text-[#D4956A] text-lg mb-2">{filteredItems[lightbox.currentIndex]?.location}</p>}<p className="text-white/60 max-w-xl mx-auto">{filteredItems[lightbox.currentIndex]?.description}</p><p className="text-white/40 text-sm mt-4">{lightbox.currentIndex + 1} / {filteredItems.length}</p></div>
          </div>
        </div>
      )}

      <section className="py-24 bg-[#2C2C2C]"><div className="max-w-4xl mx-auto px-6 text-center"><h2 className="font-serif text-4xl md:text-5xl text-white mb-6">Love What You See?</h2><p className="text-white/70 text-lg mb-8">Let's create something equally stunning for your space.</p><Link to="/contact" className="btn-copper px-8 py-3 inline-flex items-center gap-2 text-sm tracking-wider">START YOUR PROJECT <ArrowRight size={16} /></Link></div></section>
    </div>
  );
};

export default GalleryPage;
